import { memo, useLayoutEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import { ArrowDown, Sparkles, Users } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { formatTime } from '@/utils/dateTimeUtils'
import { AGENT_BUBBLE, SELF_BUBBLE } from '../../utils/participantColors'

/** Mention prefixes that get rendered as a chip inside the user bubble. */
const MENTION_PATTERNS = [
  { prefix: '@Preparation Coach', icon: Sparkles },
  { prefix: '@Group Moderator', icon: Users },
] as const

/**
 * If the message starts with a known @mention prefix, split it into
 * { chip: '@Preparation Coach', rest: 'the actual text' }.
 * Otherwise returns null (render content as-is).
 */
function parseMention(content: string) {
  for (const { prefix, icon } of MENTION_PATTERNS) {
    if (content.startsWith(prefix + ' ') || content === prefix) {
      return {
        chip: prefix,
        rest: content.slice(prefix.length).trimStart(),
        Icon: icon,
      }
    }
  }
  return null
}

/**
 * A normalized message shape so both the Preparation Coach (1-on-1) and the
 * Group Moderator (multi-user) flows can reuse the exact platform advisor-chat
 * styling.
 */
export interface CharterDisplayMessage {
  id: string
  content: string
  /** Right-aligned pink bubble — the current user's own message. */
  isUser: boolean
  /** Left-aligned light-blue bubble styled as the AI agent. */
  isAgent: boolean
  /** Stable key for the per-participant bubble colour. Null for the agent. */
  senderId?: string | null
  senderName?: string
  badge?: string
  sectionKey?: string | null
  isStreaming?: boolean
  isIntervention?: boolean
  createdAt?: string
}

const TYPING_CURSOR = '▋'

interface CharterChatMessagesProps {
  messages: CharterDisplayMessage[]
  /** Agent display name used in the avatar header (e.g. "Preparation Coach"). */
  agentName: string
  /** Called when the user scrolls near the top and older messages exist. */
  onLoadMore?: () => void
  hasMore?: boolean
  isLoadingMore?: boolean
  /** senderId -> bubble colour, from buildParticipantColors. */
  senderColors?: Record<string, string>
}

function CharterChatMessagesInner({
  messages,
  agentName,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  senderColors,
}: CharterChatMessagesProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const endRef = useRef<HTMLDivElement>(null)
  // Only auto-follow new messages when the user is already near the bottom, so
  // scrolling up to read history isn't yanked back down.
  const isNearBottomRef = useRef(true)
  // Set while we move the scrollbar ourselves, so the resulting scroll events
  // don't get mistaken for the user scrolling away.
  const selfScrollingRef = useRef(false)
  // Compared across renders to detect a prepend and measure what it added.
  const prevFirstIdRef = useRef<string | null>(null)
  const prevScrollHeightRef = useRef(0)
  const [showJumpToLatest, setShowJumpToLatest] = useState(false)

  const handleScroll = () => {
    const el = containerRef.current
    if (!el || selfScrollingRef.current) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    isNearBottomRef.current = distanceFromBottom < 120

    // Hysteresis, so the button doesn't blink while a reply streams.
    if (distanceFromBottom > 400) setShowJumpToLatest(true)
    else if (distanceFromBottom < 120) setShowJumpToLatest(false)

    if (el.scrollTop < 80 && hasMore && !isLoadingMore && onLoadMore) {
      onLoadMore()
    }
  }

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return

    const firstId = messages[0]?.id ?? null
    // Head changed and the old head is still present. Measuring now, not at
    // request time, excludes streamed growth from the prepended height.
    const prepended =
      prevFirstIdRef.current !== null &&
      firstId !== prevFirstIdRef.current &&
      messages.some((m) => m.id === prevFirstIdRef.current)

    selfScrollingRef.current = true
    if (prepended) {
      el.scrollTop += el.scrollHeight - prevScrollHeightRef.current
    } else if (isNearBottomRef.current) {
      // Instant: a restarted smooth scroll on every chunk is what juddered.
      el.scrollTop = el.scrollHeight
    }
    selfScrollingRef.current = false

    prevFirstIdRef.current = firstId
    prevScrollHeightRef.current = el.scrollHeight
  }, [messages])

  const scrollToBottom = () => {
    const el = containerRef.current
    if (!el) return
    isNearBottomRef.current = true
    setShowJumpToLatest(false)
    el.scrollTop = el.scrollHeight
  }

  return (
    <div className='relative flex flex-1 min-h-0'>
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className='overflow-auto flex-1 py-3 sm:py-4'
      >
        {isLoadingMore && (
          <div className='py-2 text-center text-xs text-muted-foreground'>
            Loading earlier messages…
          </div>
        )}
        {messages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            agentName={agentName}
            bubbleColor={
              message.senderId ? senderColors?.[message.senderId] : undefined
            }
          />
        ))}
        <div ref={endRef} />
      </div>

      {showJumpToLatest && (
        <button
          type='button'
          onClick={scrollToBottom}
          aria-label='Jump to latest message'
          className='absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center
            gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs
            font-medium text-dark-blue shadow-md transition hover:bg-gray-50'
        >
          <ArrowDown className='h-3.5 w-3.5' />
          Jump to latest
        </button>
      )}
    </div>
  )
}

// Memoized so the message list skips re-rendering (and its N per-message
// markdown renders) when only the parent's input state changes.
export const CharterChatMessages = memo(CharterChatMessagesInner)

interface MessageBubbleProps {
  message: CharterDisplayMessage
  agentName: string
  /** Per-participant colour; falls back to the agent grey when unassigned. */
  bubbleColor?: string
}

const MessageBubble = memo(function MessageBubble({
  message,
  agentName,
  bubbleColor,
}: MessageBubbleProps) {
  const { isUser, isAgent } = message

  // Set as a style, not a bg-[#…] class: Tailwind only generates arbitrary
  // values it can read statically, so a computed class would emit no CSS.
  const background = isUser
    ? SELF_BUBBLE
    : isAgent
      ? AGENT_BUBBLE
      : (bubbleColor ?? AGENT_BUBBLE)

  return (
    <div className={cn('flex mb-4', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className='max-w-[90%] sm:max-w-[80%] p-4 rounded-[20px]'
        style={{ backgroundColor: background }}
      >
        {/* Header: avatar + name + badge */}
        <div className='flex gap-3 items-center mb-2'>
          <Avatar className='w-8 h-8'>
            <AvatarImage
              src={isAgent ? '/images/avatar-chatbot.svg' : '/images/avatar-image.svg'}
              alt='Avatar'
            />
            <AvatarFallback>
              {(message.senderName || (isAgent ? agentName : 'You'))
                .slice(0, 2)
                .toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className='flex items-center'>
            <span className='flex items-center text-base font-medium text-dark-blue'>
              {message.senderName || (isAgent ? agentName : 'You')}
              {message.badge && (
                <Badge className='ml-2 pointer-events-none'>{message.badge}</Badge>
              )}
              {message.isIntervention && (
                <Badge
                  variant='outline'
                  className='ml-2 pointer-events-none border-amber-300 text-amber-700'
                >
                  ⚡ intervention
                </Badge>
              )}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className='text-sm font-medium text-black'>
          {/* Render @mention chip for user messages that start with a known mention */}
          {isUser && (() => {
            const mention = parseMention(message.content)
            if (!mention) return null
            const { chip, rest, Icon } = mention
            return (
              <div className='mb-2'>
                <span className='inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-600 text-white text-xs font-semibold'>
                  <Icon className='h-3 w-3' />
                  {chip}
                </span>
                {rest && (
                  <div className='mt-1 prose prose-sm max-w-none [&_a]:break-all [&_*]:break-words overflow-hidden'>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                      {rest}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
            )
          })()}
          {/* Regular content (no mention, or agent messages) */}
          {(!isUser || !parseMention(message.content)) && (
            <div className='prose prose-sm max-w-none [&_a]:break-all [&_*]:break-words overflow-hidden'>
              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                {message.content}
              </ReactMarkdown>
            </div>
          )}
          {message.isStreaming && TYPING_CURSOR}
        </div>

        {/* Footer */}
        <div className='flex justify-end items-center gap-2 mt-2'>
          {message.createdAt && (
            <span className='text-xs text-gray-500 opacity-75'>
              {formatTime(message.createdAt)}
            </span>
          )}
        </div>
      </div>
    </div>
  )
})
